import { ImageUtils } from "../../../ui/media/ImageUtils.js";
import { initVideo } from "../../../ui/media/MediaPlayerLayerInit.js";
import { VideoPlayerShortcuts } from "../../../ui/media/VideoPlayerShortcuts.js";
import { VideoPlayerUtils } from "../../../ui/media/VideoPlayerUtils.js";
import { guessIFDOKey, getHTMLOfIFDOIcon } from "../../../ui/media/iFDO.js";

/**
 * @param {string[]} args [src, width (optional), height (optional), srcset (optional)]
 * @param {object} data response
 * @returns {string} div placeholder
 * @memberof vef.map.popup.templateFunctions
 */
export function displayImage(args, data, postProcessingQueue) {
    const [src, width, height, srcset] = args;
    if (src == null)
        return undefined

    const id = `displayImage_${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        if (width != null && height != null) {
            const container = ImageUtils.displayImage(src, width, height, srcset);
            target.appendChild(container)
        }
        else {
            const img = new Image();
            img.onload = () => {
                const container = ImageUtils.displayImage(src, img.width, img.height, srcset);
                target.appendChild(container);
            };
            img.src = src;
            if (srcset != null)
                img.srcset = srcset;
        }
    });
    return `<div class="image-wrapper" id="${id}"></div>`;
}

// static video player instance
let idDisplayVideoPrefix = 'displayVideo_';

/**
 * @param {string[]} args [media, width, height, thumbnail (optional), cover (optional), poster (optional)]
 * The following keys are supported: "media" (url to media file), thumbnail (url to webvtt file for seekbar thumbnails), cover (url to video cover) and poster (url to background poster on video player loading). They contain the respective urls. 
 * @param {object} data response
 * @returns {string} div placeholder
 * @memberof vef.map.popup.templateFunctions
 */
export function displayVideo(args, data, postProcessingQueue) {
    const [media, width, height, thumbnail, cover, poster] = args;

    if (media == null)
        return undefined

    const id = `${idDisplayVideoPrefix}${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        const video = initVideo({
            'media': media,
            'thumbnail': thumbnail,
            'cover': cover,
            'poster': poster || VideoPlayerUtils.getPoster(),
            'initPlayerWithKeyFocus': true
        });
        const videoContainer = document.createElement('div');
        if (width && height) {
            videoContainer.style = `width: 100%; height: 0; padding-bottom: calc(100% * ${height}/${width}); position: relative;`
            video.style = 'position: absolute; width: 100%; height: 100%';
        }
        else {
            console.warn('The video player is initialized without width and height. As a result, the dimensions of the video player are not known until the video is loaded. This can lead to sudden changes in the layout when the video player is displayed.')
        }
        videoContainer.append(video)
        target.appendChild(videoContainer)

    });
    return `<div id="${id}"></div>`;
}

// static video player keyboard shortcut instance
let shortcutsWindow = null;

/**
 * @param {string[]} args [src, width (optional), height (optional), srcset (optional)]
 * @param {object} data response
 * @returns {string} div placeholder
 * @memberof vef.map.popup.templateFunctions
 */
export function displayVideoPlayerKeys(args, data, postProcessingQueue) {

    const id = `displayVideoPlayerKeys_${postProcessingQueue.length}`;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        const keyboardShortcutLink = document.createElement("a");
        keyboardShortcutLink.style = "display: inline-block; text-decoration: none;";
        keyboardShortcutLink.href = "#";
        keyboardShortcutLink.innerHTML = "<i class='fas fa-keyboard'></i> Keyboard Shortcuts";
        keyboardShortcutLink.onclick = e => {
            e.preventDefault();
            if (!shortcutsWindow)
                shortcutsWindow = new VideoPlayerShortcuts();
            if (!shortcutsWindow.isOpened)
                shortcutsWindow.open();
            else
                shortcutsWindow.close();
        }

        const videoPlayerElement = container.querySelector(`[id^=${idDisplayVideoPrefix}]`)
        if (!videoPlayerElement)
            target.style.display = 'None';

        target.appendChild(keyboardShortcutLink);
    });
    return `<div id="${id}"></div>`;
}

/**
 * @param {string[]} args []
 * @param {object} data response
 * @returns {string} div placeholder
 * @memberof vef.map.popup.templateFunctions
 */
export function displayIFDOIcons(args, data, postProcessingQueue) {

    const id = `displayIFDOIcons_${postProcessingQueue.length}`;
    const featureProperties = data.properties;

    postProcessingQueue.push(container => {
        const target = container.querySelector("#" + id);
        if (!target) return;

        const containerElement = document.createElement('div');
        containerElement.classList.add('flex-container-ifdo-icon');
        let containerChildrenHTML = "";

        for (const [featurePropertiesKey, featurePropertiesValue] of Object.entries(featureProperties)) {
            const iFDOKey = guessIFDOKey(featurePropertiesKey);
            const iFDOValue = featurePropertiesValue;
            if (iFDOKey && iFDOValue)
                containerChildrenHTML += getHTMLOfIFDOIcon(iFDOKey, iFDOValue);
        }

        containerElement.innerHTML = containerChildrenHTML;
        target.appendChild(containerElement);
    });
    return `<div id="${id}"></div>`;
}